
import { FiEdit2, FiLoader, FiPlus, FiRefreshCw, FiX } from 'react-icons/fi';
import { formatDateOnly } from '@/lib/utils/dateUtils';
import type { FoundStock, FoundUnit } from '../zoneCountSteps';

/** A short label for what the records say about a found unit. */
export const foundStatusLabel = (found: FoundUnit): string => {
  if (!found.lookup) return found.lookupFailed ? 'Could not check' : 'Checking…';
  switch (found.lookup.status) {
    case 'NOT_IN_SYSTEM':
      return 'Not in the system';
    case 'STOCKED_ELSEWHERE':
      return `Recorded in ${found.lookup.recordedZone?.name || 'another zone'}`;
    case 'RECEIVED':
      return 'Received, never stocked';
    case 'PICKED':
      return found.lookup.salesOrderNumber
        ? `Picked for SO #${found.lookup.salesOrderNumber}`
        : 'Picked for an order';
    case 'QUARANTINED':
      return 'Already quarantined';
    case 'SHIPPED':
      return 'Recorded as shipped';
    case 'DESTROYED':
      return 'Recorded as destroyed';
    case 'MARKED_NOT_RECEIVED':
      return 'Marked not received';
    case 'IN_THIS_ZONE':
      return 'Recorded here — refresh';
    case 'OTHER_LOCATION':
      return 'Recorded at another location';
    default:
      return 'Cannot be recorded';
  }
};

/** Whether a found unit is one the review can act on (quarantine, or record here). */
export const foundActionable = (found: FoundUnit) =>
  !!found.lookup && (found.lookup.canQuarantine || found.lookup.canMoveHere);

type ZoneCountFoundListProps = {
  found: FoundUnit[];
  foundStock: FoundStock[];
  onRemoveUnit: (key: string) => void;
  onRetryLookup: (key: string) => void;
  onEditStock: (stock: FoundStock) => void;
  onRemoveStock: (id: string) => void;
  onAddStock: () => void;
};

/**
 * Everything counted on the shelf that this zone has no record of: serialized units (each with what
 * the records say about it, looked up as it is scanned) and bulk stock of lots not listed. The
 * review decides what to do with them; here they can only be taken back off the count.
 */
function ZoneCountFoundList({
  found,
  foundStock,
  onRemoveUnit,
  onRetryLookup,
  onEditStock,
  onRemoveStock,
  onAddStock
}: ZoneCountFoundListProps) {
  const total = found.length + foundStock.reduce((sum, s) => sum + s.quantity, 0);
  return (
    <section className="zc-found" aria-label="Not on record in this zone">
      <div className="zc-section-head">
        <h3>Not on record in this zone</h3>
        {total > 0 && <span className="zc-pill zc-pill--warn">{total}</span>}
      </div>
      {total === 0 && (
        <p className="zc-found__empty">
          Units you scan that this zone has no record of are listed here.
        </p>
      )}
      {found.map((unit) => (
        <div
          key={unit.key}
          className={[
            'zc-found__row',
            unit.lookup && !foundActionable(unit) && 'zc-found__row--blocked'
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <div className="zc-found__main">
            <span className="zc-found__name">
              {unit.lookup?.product?.productName || 'Serialized unit'}
            </span>
            <span className="zc-mono">Serial {unit.serialNumber}</span>
            <span className="zc-found__status">
              {!unit.lookup && !unit.lookupFailed && <FiLoader aria-hidden className="zc-spin" />}
              {foundStatusLabel(unit)}
            </span>
          </div>
          <div className="zc-found__actions">
            {unit.lookupFailed && (
              <button
                type="button"
                className="zc-icon-button"
                aria-label={`Check serial ${unit.serialNumber} again`}
                onClick={() => onRetryLookup(unit.key)}
              >
                <FiRefreshCw />
              </button>
            )}
            <button
              type="button"
              className="zc-icon-button"
              aria-label={`Take serial ${unit.serialNumber} off the count`}
              onClick={() => onRemoveUnit(unit.key)}
            >
              <FiX />
            </button>
          </div>
        </div>
      ))}
      {foundStock.map((stock) => (
        <div key={stock.id} className="zc-found__row">
          <div className="zc-found__main">
            <span className="zc-found__name">{stock.product.productName}</span>
            <span className="zc-found__status">
              Lot {stock.lot || '—'} · Exp {formatDateOnly(stock.expiration) || '—'} ·{' '}
              <strong>{stock.quantity}</strong> counted
            </span>
          </div>
          <div className="zc-found__actions">
            <button
              type="button"
              className="zc-icon-button"
              aria-label={`Edit found ${stock.product.productName}`}
              onClick={() => onEditStock(stock)}
            >
              <FiEdit2 />
            </button>
            <button
              type="button"
              className="zc-icon-button"
              aria-label={`Take found ${stock.product.productName} off the count`}
              onClick={() => onRemoveStock(stock.id)}
            >
              <FiX />
            </button>
          </div>
        </div>
      ))}
      <button type="button" className="zc-add-found" onClick={onAddStock}>
        <FiPlus aria-hidden /> Add found stock (OTC / no serial)
      </button>
    </section>
  );
}

export default ZoneCountFoundList;
